// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@eisinc/App/Components/Notify";
import { ScheduledLinesContext, ScheduledLinesContextModel } from "@eisinc/App/Contexts/ScheduledLinesContext";
import getScheduledLines from "@eisinc/App/Handlers/getScheduledLines";
import { loadingOverflow } from "@eisinc/App/SharedStyles/GeneralStyles";
import { CustomAppState } from "@eisinc/App/Store/Reducers";
import Zone from "@insite/client-framework/Components/Zone";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import PageProps from "@insite/client-framework/Types/PageProps";
import LoadingOverlay from "@insite/mobius/LoadingOverlay";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Page from "@insite/mobius/Page";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const { cartId } = state.pages.checkoutShipping;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);

    return {
        isCartLoading: cartState?.isLoading,
        isLoadingScheduledLines: state.xCommon.isLoadingScheduledLines,
    };
};

const mapDispatchToProps = {
    getScheduledLines,
};

type Props = PageProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ScheduleOrderPageStyles {
    spinner?: LoadingSpinnerProps;
    loadingOverlay?: InjectableCss;
}

export const scheduleOrderPageStyles: ScheduleOrderPageStyles = {
    spinner: {
        color: "primary.main",
        css: css`
            margin: 0 auto;
        `,
    },
    loadingOverlay: { ...loadingOverflow },
};

const styles = scheduleOrderPageStyles;

const ScheduleOrderPage = ({ id, isCartLoading, isLoadingScheduledLines, getScheduledLines }: Props) => {
    const [scheduledLinesResult, setScheduledLinesResult] = React.useState<ScheduledLinesContextModel>();

    React.useEffect(() => {
        if (isLoadingScheduledLines || scheduledLinesResult?.groups?.length) return;

        getScheduledLines({
            onError: (errorMessage: string) => {
                setScheduledLinesResult({ errorMessage });
            },
            onComplete(resultProps) {
                if (resultProps.apiResult?.successful) {
                    const { cartId, canCustomerScheduleOrders, hasScheduledDeliveries, groups } =
                        resultProps.apiResult.result;
                    setScheduledLinesResult({ cartId, canCustomerScheduleOrders, hasScheduledDeliveries, groups });
                }
            },
        });
    }, []);

    const isLoading = isCartLoading || isLoadingScheduledLines;

    return (
        <Page>
            <LoadingOverlay {...styles.loadingOverlay} loading={isLoading}>
                {scheduledLinesResult?.canCustomerScheduleOrders ? (
                    <ScheduledLinesContext.Provider value={scheduledLinesResult}>
                        <Zone contentId={id} zoneName="Content" />
                    </ScheduledLinesContext.Provider>
                ) : (
                    !isLoading && <Notify body={siteMessage("Custom_SheduleOrder_Not_Enabled")} messageType="warning" />
                )}
            </LoadingOverlay>
        </Page>
    );
};
const scheduleOrderPageModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(ScheduleOrderPage),
    definition: {
        hasEditableUrlSegment: true,
        hasEditableTitle: true,
        pageType: "System",
        fieldDefinitions: [],
    },
};

export default scheduleOrderPageModule;

/**
 * @deprecated Use string literal "ScheduleOrderPage" instead of this constant.
 */
export const ScheduleOrderPageContext = "ScheduleOrderPage";
